import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type { TemplateVersion } from '../../types/templateModel'
import { TemplateParentLink } from '../templateParentLink/TemplateParentLink'
import { TemplateReferenceChangeModal } from '../updateTemplateReference/TemplateReferenceChangeModal'
import { useTemplateParentOptions } from './useTemplateParentOptions'

type Props = {
  isEditable?: boolean
}

export const TemplateVersionParent = ({ isEditable }: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()
  const [isChangeParentModelOpen, setIsChangeParentModalOpen] = useState(false)
  const options = useTemplateParentOptions(templateVersion)
  const value = useMemo(
    () =>
      (options ?? []).find((t) => t === templateVersion?.versionInfo.parentId),
    [options, templateVersion?.versionInfo.parentId],
  )

  assertNotEmpty(templateVersion)

  const {
    versionInfo: { parentId },
  } = templateVersion

  const handleChangeTemplateParent = async (nextValue: Optional<string>) => {
    assertNotEmpty(templateVersion)

    const nextTemplateVersion = {
      ...templateVersion,
      versionInfo: {
        ...templateVersion.versionInfo,
        parentId: nextValue,
      },
    } as TemplateVersion
    await templateVersion.updateTemplate(nextTemplateVersion)
    setIsChangeParentModalOpen(false)
  }

  return (
    <div className="grid max-w-md grid-cols-2 items-center gap-x-2">
      {parentId ? <TemplateParentLink templateId={parentId} /> : '-'}
      {isEditable && (
        <>
          <Button onClick={() => setIsChangeParentModalOpen(true)}>
            {translate(
              'templateViewer.templateVersionDetails.changeParentButton.label',
            )}
          </Button>
          <TemplateReferenceChangeModal
            title={translate(
              'templateViewer.templateVersionDetails.changeParentModal.title',
            )}
            options={options}
            value={value}
            onChange={handleChangeTemplateParent}
            isOpen={isChangeParentModelOpen}
            onClose={() => setIsChangeParentModalOpen(false)}
          />
        </>
      )}
    </div>
  )
}
